/**
 * Per-browser appearance preferences (theme mode, font size, accent hue).
 *
 * Stored in localStorage — no server round-trip. The dashboard reads the
 * settings on boot in `main.tsx` so the right theme applies before first
 * paint (no FOUC flash of light mode for dark-mode users).
 *
 * Apply mechanism:
 *  - mode: toggles a `.dark` class on <html>; CSS variables in styles.css
 *    cascade from there.
 *  - font_scale: sets `--kz-font-scale` on <html>, which css consumes via
 *    `font-size: calc(... * var(--kz-font-scale))` on the body.
 *  - accent: overrides `--kz-color-brand-primary` (and a few derivatives)
 *    via inline CSS variables on <html>.
 */
export type ThemeMode = 'light' | 'dark' | 'system';
export type FontSize = 'small' | 'normal' | 'large';

export interface AccentPreset {
  id: string;
  label: string;
  primary: string;
  secondary: string;
}

export const ACCENT_PRESETS: AccentPreset[] = [
  { id: 'indigo',  label: 'Indigo',  primary: '#4f6bff', secondary: '#7a5cff' },
  { id: 'coral',   label: 'Coral',   primary: '#ff7a59', secondary: '#ec4899' },
  { id: 'emerald', label: 'Emerald', primary: '#10b981', secondary: '#06b6d4' },
  { id: 'amber',   label: 'Amber',   primary: '#f59e0b', secondary: '#ef4444' },
  { id: 'violet',  label: 'Violet',  primary: '#a855f7', secondary: '#ec4899' },
];

export interface AppearancePrefs {
  mode: ThemeMode;
  font_size: FontSize;
  accent_id: string;
}

const STORAGE_KEY = 'kidzio.appearance.v1';
const DEFAULT_PREFS: AppearancePrefs = {
  mode: 'system',
  font_size: 'normal',
  accent_id: 'indigo',
};

const FONT_SCALE: Record<FontSize, string> = {
  small: '0.92',
  normal: '1',
  large: '1.12',
};

export function loadAppearancePrefs(): AppearancePrefs {
  if (typeof localStorage === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<AppearancePrefs>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function saveAppearancePrefs(prefs: AppearancePrefs): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / private mode — silently fall back to in-memory */
  }
}

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Set DOM-level CSS variables + classes from the given prefs. */
export function applyAppearancePrefs(prefs: AppearancePrefs): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;

  // Mode → <html class="dark">.
  const resolved = resolveMode(prefs.mode);
  html.classList.toggle('dark', resolved === 'dark');

  // Font size → CSS variable consumed by body { font-size: ... }.
  html.style.setProperty('--kz-font-scale', FONT_SCALE[prefs.font_size]);

  // Accent → override the brand color tokens.
  const accent = ACCENT_PRESETS.find((p) => p.id === prefs.accent_id) ?? ACCENT_PRESETS[0]!;
  html.style.setProperty('--kz-color-brand-primary', accent.primary);
  html.style.setProperty('--kz-color-brand-secondary', accent.secondary);
}

/** Re-apply when system theme flips and the user is on 'system' mode. */
export function watchSystemTheme(getMode: () => ThemeMode): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  if (!mq) return () => undefined;
  const handler = () => {
    if (getMode() === 'system') {
      document.documentElement.classList.toggle('dark', mq.matches);
    }
  };
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
